import React, { useEffect, useState } from 'react';
import { ActionMode, GeneralStatusEnum } from '../common/enums/common.enum';
import { createGroup, updateGroup } from '../api/kindergarten.api';
import { Box, Modal, Typography } from '@mui/material';
import { CreateGroupDto, GroupDto } from '../common/interfaces/kindergarten.interface';
import { createNotification } from '../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../common/constants/common.constants';
import GenericButton, { GenericButtonTypeEnum } from './GenericButton';
import GenericTextField from './GenericTextField/GenericTextField';
import GenericStatusSwitch from './GenericStatusSwitch';
import { useTranslation } from 'react-i18next';

export interface GroupModalProps {
  open: boolean;
  onClose: () => void;
  kindergartenId: string;
  locationId?: string;
  group?: GroupDto;
  mode: ActionMode;
  onCreateOrUpdate?: (group: GroupDto, mode: ActionMode) => void;
  bulkMode?: boolean;
  onSubmit?: (groupData: CreateGroupDto, keepOpen: boolean) => void;
  onDelete?: () => void;
  locationStatus?: GeneralStatusEnum;
}

const GroupModal: React.FC<GroupModalProps> = ({
  open,
  onClose,
  kindergartenId,
  locationId,
  group,
  mode,
  onCreateOrUpdate,
  bulkMode = false,
  onSubmit,
  onDelete,
  locationStatus
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [status, setStatus] = useState<GeneralStatusEnum>(group?.status ?? GeneralStatusEnum.ACTIVE);
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    if (group) {
      setName(group.name || '');
      setDescription(group.description || '');
      setStatus(group.status ?? GeneralStatusEnum.ACTIVE);
    } else {
      setName('');
      setDescription('');
      setStatus(GeneralStatusEnum.ACTIVE);
    }
  }, [group]);

  const handleSubmit = async (keepOpen: boolean = false) => {
    try {
      const groupData = {
        id: group?.id,
        name: name.trim(),
        description: description?.trim() || undefined,
        status: status
      };
      if (name.trim() === '') {
        setNameError(true);
        return;
      }
      setNameError(false);

      if (mode === ActionMode.CREATE) {
        const groupData = {
          name: name.trim(),
          description: description?.trim() || undefined
        };
        if (bulkMode) {
          if (onSubmit) onSubmit(groupData, keepOpen);
          setName('');
          setDescription('');
          if (!keepOpen) onClose();
          return;
        }

        if (!locationId) {
          return;
        }
        const response = await createGroup(kindergartenId, locationId, {
          name: name.trim(),
          description: description?.trim() || undefined,
          status: locationStatus ?? GeneralStatusEnum.ACTIVE
        });
        createNotification(MESSAGE_TYPE.SUCCESS, t('groups.saved'));
        if (onCreateOrUpdate && response.results) onCreateOrUpdate(response.results, mode);

        setName('');
        setDescription('');
        if (!keepOpen) onClose();
      } else if (mode === ActionMode.EDIT) {
        if (bulkMode) {
          if (onSubmit) onSubmit(groupData, keepOpen);
          if (!keepOpen) onClose();
          return;
        }

        if (!group || !group.id) {
          return;
        }
        const payload = {
          name: name.trim(),
          description: description.trim() || undefined,
          status: status
        };
        const response = await updateGroup(kindergartenId, group?.id, payload);
        createNotification(MESSAGE_TYPE.SUCCESS, t('groups.saved'));
        if (onCreateOrUpdate && response.results) onCreateOrUpdate(response.results, mode);
        if (!keepOpen) onClose();
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('groups.saveFailed'));
    }
  };

  const handleCreateAndKeepOpen = async () => {
    await handleSubmit(true);
    setName('');
    setDescription('');
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          p: 3,
          pt: 2,
          borderRadius: 2,
          width: 500,
          outline: 'none',
          border: 'none'
        }}
      >
        <Typography variant="h6">{mode === ActionMode.CREATE ? t('groups.create') : t('groups.edit')}</Typography>

        <Box
          sx={{
            display: 'flex',
            padding: 0,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '14px',
            width: '100%',
            alignSelf: 'stretch'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              width: '100%'
            }}
          >
            <GenericTextField
              label={t('groups.name')}
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (e.target.value.trim() !== '') {
                  setNameError(false);
                }
              }}
              required
              error={nameError}
            />

            <GenericTextField
              label={t('common.description')}
              value={description}
              onChange={e => setDescription(e.target.value)}
              multiline
              rows={3}
            />
          </Box>
        </Box>

        {mode === ActionMode.EDIT && !bulkMode && (
          <GenericStatusSwitch
            label={t('groups.status')}
            status={status}
            onChange={newStatus => setStatus(newStatus)}
            disabled={locationStatus === GeneralStatusEnum.INACTIVE}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            mt: 2
          }}
        >
          {mode === ActionMode.CREATE && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              onClick={handleCreateAndKeepOpen}
              label={t('common.saveAndCreateAnother')}
            />
          )}

          {mode === ActionMode.EDIT && bulkMode && onDelete && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Delete}
              onClick={() => {
                onDelete();
              }}
              label={t('common.delete')}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              marginLeft: 'auto'
            }}
          >
            <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="text" onClick={onClose} label={t('common.cancel')} />
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              isActive={name.trim() !== ''}
              onClick={() => handleSubmit(false)}
              label={t('common.save')}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
export default GroupModal;
